import { Button, Table } from "antd";
import moment from "moment";
import React, { useRef, useState } from "react";

import InvoiceIcon from "../../../../svgs/invoice.svg?react";
import {
  IVatDeclarationUser,
  NextVatDeclarationStatus,
} from "../../../../types";
import { formatAssignee } from "../../../../utils/assignee";
import { formatPaymentFrequency } from "../../../../utils/paymentFrequency";
import { onClickStopPropagation } from "../../../../utils/table";
import AssigneeFilter from "../../../common/AssigneeFilter";
import EmptyWrapper from "../../../common/EmptyWrapper";
import { InvoiceIconWrapper } from "../../../common/styledComponents";
import { KontaxUserContext } from "../../../contexts/KontaxUserContext";
import UserFilters, { UserFilterOptions } from "../UserFilters";
import { NextDeclarationDue } from "./NextDeclarationDue/NextDeclarationDue";
import { nextDeclarationDueSort } from "./NextDeclarationDue/helpers";
import { NextDeclarationStatus } from "./NextDeclarationStatus";
import VatDeclarationDetails from "./VatDeclarationDetails/index";
import { VatLastCheckedAt } from "./VatLastCheckedAt";
import { vatTableColumnSorter } from "./helpers";
import { DISPLAY_DATE_FORMAT } from "@/v1/constants";

interface Props {
  users: IVatDeclarationUser[] | null;
  loading: boolean;
  filterOptions: UserFilterOptions;
  handleFilterChange: Function;
  handlePageChange: Function;
  pageNumber: number;
  loadAll: () => void;
  onSelectCustomer: (email: string) => void;
}

const VatTable = ({
  users,
  loading,
  filterOptions,
  pageNumber,
  handleFilterChange,
  handlePageChange,
  loadAll,
  onSelectCustomer,
}: Props) => {
  interface UpdatedDetail {
    email: string;
    hasNotes?: boolean;
    status?: NextVatDeclarationStatus;
  }
  const [updatedDetails, setUpdatedDetails] = useState<Array<UpdatedDetail>>(
    []
  );
  const filterOptionsRef = useRef(filterOptions);
  filterOptionsRef.current = filterOptions;
  const handleFilterChangeRef = useRef<Function>(handleFilterChange);
  handleFilterChangeRef.current = handleFilterChange;
  // Expanding a row selects the customer for the ELSTER / Submissions / DFV
  // tabs (via `onSelectCustomer`, which only writes the `email` selection
  // param) — it must not filter or otherwise change the VAT list. SMT-1354.
  const handleRowExpand = (expanded: boolean, record: { email: string }) => {
    if (expanded) {
      onSelectCustomer(record.email);
    }
  };

  const data = React.useMemo(
    () =>
      users?.map((user) => {
        const {
          accountId,
          firstName,
          lastName,
          legalName,
          email,
          frequency,
          nextDeclarationDueDate,
          nextDeclarationDuePeriod,
          nextDeclarationDueYear,
          vatLastCheckedAt,
          status,
          lastVATDeclarationSent,
          unbookedTransactions,
          unbookedTransactionsWithoutReceipts,
          hasNotes,
          kontaxUser,
        } = user;

        const hasNewNotes =
          hasNotes ||
          updatedDetails?.find(
            (updatedDetails) => updatedDetails.email === user.email
          )?.hasNotes;

        const updatedStatus =
          updatedDetails?.find(
            (updatedDetails) => updatedDetails.email === user.email
          )?.status || status;

        return {
          accountId,
          firstName,
          lastName,
          legalName: legalName || "-",
          email,
          frequency,
          nextDeclarationDuePeriod,
          nextDeclarationDueYear,
          unbookedTransactions,
          hasNotes: hasNewNotes,
          unbookedTransactionsWithoutReceipts,
          nextDeclarationDueDate: nextDeclarationDueDate
            ? moment(nextDeclarationDueDate).format(DISPLAY_DATE_FORMAT)
            : "",
          vatLastCheckedAt: vatLastCheckedAt ? moment(vatLastCheckedAt) : null,
          status: updatedStatus,
          lastVATDeclarationSent: lastVATDeclarationSent
            ? moment(lastVATDeclarationSent).format(DISPLAY_DATE_FORMAT)
            : "",
          kontaxUser,
        };
      }),
    [updatedDetails, users]
  );

  const columns = React.useMemo(
    () => [
      {
        title: "Notes",
        key: "hasNotes",
        render: (row: IVatDeclarationUser) => {
          const { hasNotes } = row;
          return hasNotes ? (
            <InvoiceIconWrapper>
              <InvoiceIcon />
            </InvoiceIconWrapper>
          ) : null;
        },
      },
      {
        title: "Name",
        key: "lastName",
        render: (row: IVatDeclarationUser) => {
          const { legalName, email } = row;
          return (
            <React.Fragment>
              <div>{legalName || "-"}</div>
              <div>{email}</div>
            </React.Fragment>
          );
        },
        sorter: (a: IVatDeclarationUser, b: IVatDeclarationUser) =>
          vatTableColumnSorter(a.legalName || "-", b.legalName || "-"),
      },
      {
        title: "Next declaration due",
        key: "nextDeclarationDue",
        render: (row: IVatDeclarationUser) => {
          const {
            email,
            nextDeclarationDuePeriod,
            nextDeclarationDueYear,
            frequency,
          } = row;

          return (
            <NextDeclarationDue
              email={email}
              vatPaymentFrequency={frequency}
              nextDeclarationDueYear={nextDeclarationDueYear}
              nextDeclarationDuePeriod={nextDeclarationDuePeriod}
              setUpdatedDetails={(status: NextVatDeclarationStatus) =>
                setUpdatedDetails(updatedDetails.concat({ email, status }))
              }
            />
          );
        },
        sorter: nextDeclarationDueSort,
        onCell: onClickStopPropagation,
      },
      {
        title: "Next declaration due date",
        dataIndex: "nextDeclarationDueDate",
        key: "nextDeclarationDueDate",
        sorter: (a: IVatDeclarationUser, b: IVatDeclarationUser) =>
          vatTableColumnSorter(
            a.nextDeclarationDueDate,
            b.nextDeclarationDueDate
          ),
      },
      {
        title: "Frequency",
        key: "frequency",
        render: (row: IVatDeclarationUser) =>
          formatPaymentFrequency(row.frequency),
        sorter: (a: IVatDeclarationUser, b: IVatDeclarationUser) =>
          vatTableColumnSorter(a.frequency, b.frequency),
      },
      {
        title: "Assignee",
        key: "assigneeEmail",
        dataIndex: "kontaxUser",
        width: 120,
        render: (kontaxUser: IVatDeclarationUser["kontaxUser"]) =>
          formatAssignee(
            kontaxUser?.assigneeDisplayName,
            kontaxUser?.assigneeEmail
          ),
        filterDropdown: ({ confirm }: { confirm: () => void }) => (
          <div style={{ padding: 8 }}>
            <AssigneeFilter
              value={filterOptionsRef.current.assigneeId ?? undefined}
              onChange={(val) => {
                handleFilterChangeRef.current({
                  ...filterOptionsRef.current,
                  assigneeId: val,
                });
                confirm();
              }}
            />
          </div>
        ),
      },
      {
        title: "Last checked",
        key: "vatLastCheckedAt",
        render: (row: IVatDeclarationUser) => {
          const { email, vatLastCheckedAt } = row;
          return (
            <VatLastCheckedAt
              email={email}
              vatLastCheckedAt={vatLastCheckedAt}
            />
          );
        },
        sorter: (a: IVatDeclarationUser, b: IVatDeclarationUser) =>
          vatTableColumnSorter(
            a.vatLastCheckedAt?.format(DISPLAY_DATE_FORMAT),
            b.vatLastCheckedAt?.format(DISPLAY_DATE_FORMAT)
          ),
        onCell: onClickStopPropagation,
      },
      {
        title: "Status",
        key: "status",
        render: (row: IVatDeclarationUser) => {
          const { email, status } = row;
          return (
            <NextDeclarationStatus
              email={email}
              status={status}
              setUpdatedDetails={(status: NextVatDeclarationStatus) =>
                setUpdatedDetails(updatedDetails.concat({ email, status }))
              }
            />
          );
        },
        sorter: (a: IVatDeclarationUser, b: IVatDeclarationUser) =>
          vatTableColumnSorter(a.status, b.status),
        onCell: onClickStopPropagation,
      },
      {
        title: "Last VAT- declaration sent",
        dataIndex: "lastVATDeclarationSent",
        key: "lastVATDeclarationSent",
        sorter: (a: IVatDeclarationUser, b: IVatDeclarationUser) =>
          vatTableColumnSorter(
            a.lastVATDeclarationSent,
            b.lastVATDeclarationSent
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const renderRowSubComponent = React.useCallback(
    (row: IVatDeclarationUser) => {
      return (
        <KontaxUserContext email={row.email}>
          <VatDeclarationDetails
            setUpdatedDetails={(newDetails: UpdatedDetail) =>
              setUpdatedDetails(updatedDetails.concat(newDetails))
            }
          />
        </KontaxUserContext>
      );
    },
    [updatedDetails]
  );

  return (
    <>
      <UserFilters
        filterOptions={filterOptions}
        setFilterOptions={(
          updatedFilterOptions: Partial<UserFilterOptions>
        ) => {
          handleFilterChange({
            ...filterOptions,
            ...updatedFilterOptions,
          });
        }}
        loadAll={loadAll}
      />
      <>
        <Table
          columns={columns}
          dataSource={data ?? []}
          loading={loading}
          bordered
          expandable={{
            expandedRowRender: renderRowSubComponent,
            expandRowByClick: true,
            onExpand: handleRowExpand,
          }}
          pagination={false}
          scroll={{ x: "100%" }}
          rowKey="accountId"
          locale={{
            emptyText: (
              <EmptyWrapper
                description={
                  users === null
                    ? "Enter an email or click “Load All” to see VAT declarations."
                    : filterOptions.email
                    ? "No active VAT declarations found for this email. The customer may be in Dunning Block or have an inactive Fibu period."
                    : "No users found"
                }
              />
            ),
          }}
        />
        {!!users?.length && (
          <>
            {Number(pageNumber) !== 1 && (
              <Button
                onClick={() => {
                  handlePageChange(Number(pageNumber) - 1);
                }}
                type="primary"
              >
                Previous
              </Button>
            )}
            <span style={{ paddingRight: "10px" }}>Page: {pageNumber}</span>
            <Button
              onClick={() => {
                handlePageChange(Number(pageNumber) + 1);
              }}
              type="primary"
            >
              Next
            </Button>
          </>
        )}
      </>
    </>
  );
};

export default VatTable;
